import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faCheck,
  faCheckCircle,
  faInfoCircle,
  faFillDrip,
} from "@fortawesome/free-solid-svg-icons";

/**
 * RepairProcessTable Component
 *
 * Displays repair project elements with inspection data and repair processes.
 */
const RepairProcessTable = ({
  spans = [],
  onManagePanelBays,
  onMarkCompleted,
  onMarkUndamaged,
  projectStatus,
  projectStarted = false,
  completionActionElementId = null,
  searchTerm = "",
  filterStatus = "all",
  elementLabelSingular = "Element",
  elementLabelPlural = "Elements",
  parentGroupLabelSingular = "Parent",
}) => {
  const [expandedSpans, setExpandedSpans] = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  const handleManagePanelBays = (span) => {
    if (typeof onManagePanelBays === "function") {
      onManagePanelBays(span);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-CA", {
      style: "currency",
      currency: "CAD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatNumber = (value, decimals = 2) =>
    new Intl.NumberFormat("en-CA", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value || 0);

  const toNumber = (value) => {
    if (value === null || value === undefined || value === "") {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-CA");
  };

  const getElementAreaSummary = (element) => {
    const totalArea = toNumber(element.total_area ?? element.totalArea) ?? 0;
    const estimatedPaintedArea =
      toNumber(
        element.estimated_painted_area ??
          element.estimatedPaintedArea ??
          element.estimated_repair_area ??
          element.estimatedRepairArea,
      ) ?? 0;
    const savedActualPaintedArea = toNumber(
      element.actual_painted_area ?? element.actualPaintedArea,
    );
    const savedActualPaintedPercentage = toNumber(
      element.actual_painted_area_percent ?? element.actualPaintedAreaPercent,
    );
    const hasSavedActualPaintedArea =
      savedActualPaintedArea !== null || savedActualPaintedPercentage !== null;
    const paintedArea = hasSavedActualPaintedArea
      ? (savedActualPaintedArea ??
        (totalArea > 0 && savedActualPaintedPercentage !== null
          ? (totalArea * savedActualPaintedPercentage) / 100
          : estimatedPaintedArea))
      : estimatedPaintedArea;

    return {
      totalArea,
      paintedArea,
      hasSavedActualPaintedArea,
    };
  };

  const toggleSpanExpansion = (spanId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setExpandedSpans((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(spanId)) {
        newSet.delete(spanId);
      } else {
        newSet.add(spanId);
      }
      return newSet;
    });
  };

  const toggleGroupExpansion = (groupKey, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const matchesSearch = (element) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    const code = element.element_code || element.span_code || "";
    const name = element.element_name || element.span_name || "";
    const parent = element.parent_name || element.frame_name || "";

    return (
      code.toLowerCase().includes(search) ||
      name.toLowerCase().includes(search) ||
      parent.toLowerCase().includes(search)
    );
  };

  const matchesFilter = (element) => {
    if (filterStatus === "all") return true;
    const status = element.element_status || element.span_status || "pending";
    return status === filterStatus;
  };

  const getStatusBadge = (status, completionType) => {
    const badges = {
      pending: {
        color: "bg-yellow-100 text-yellow-800",
        icon: faInfoCircle,
        text: "Pending",
      },
      undamaged: {
        color: "bg-emerald-100 text-emerald-800",
        icon: faCheckCircle,
        text: "Undamaged",
      },
      completed: {
        color: "bg-green-100 text-green-800",
        icon: faCheckCircle,
        text: "Completed",
      },
    };

    const badge =
      status === "completed" && completionType === "undamaged"
        ? badges.undamaged
        : badges[status] || badges.pending;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.color}`}
      >
        <FontAwesomeIcon icon={badge.icon} className="w-3 h-3 mr-1" />
        {badge.text}
      </span>
    );
  };

  const getUrgencyBadge = (urgency) => {
    const badges = {
      critical: { color: "bg-red-100 text-red-800", text: "Critical" },
      high: { color: "bg-orange-100 text-orange-800", text: "High" },
      medium: { color: "bg-yellow-100 text-yellow-800", text: "Medium" },
      low: { color: "bg-green-100 text-green-800", text: "Low" },
    };

    const badge = badges[urgency] || badges.medium;

    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${badge.color}`}
      >
        {badge.text}
      </span>
    );
  };

  const getDisplayRepairProcesses = (span) => {
    const processes = Array.isArray(span.repair_processes)
      ? span.repair_processes
      : [];
    const groupedProcesses = new Map();

    processes.forEach((process, index) => {
      const processName =
        process.process_name || process.processName || "Process";
      const processKey =
        process.processTypeId ||
        process.process_type_id ||
        processName ||
        `process-${index}`;
      const estimatedArea = Number(process.estimated_area || 0);
      const estimatedCost =
        Number(process.estimated_cost || 0) ||
        Number(process.unit_cost || 0) * estimatedArea;
      const sequenceOrder = Number(
        process.sequenceOrder || process.sequence_order || index + 1,
      );
      const layerFactor = Number(process.layer_factor || 1);

      if (!groupedProcesses.has(processKey)) {
        groupedProcesses.set(processKey, {
          processKey,
          processName,
          sequenceOrder,
          estimatedArea: 0,
          estimatedCost: 0,
          isRequired: false,
          layerNotes: new Set(),
        });
      }

      const groupedProcess = groupedProcesses.get(processKey);
      groupedProcess.estimatedArea += estimatedArea;
      groupedProcess.estimatedCost += estimatedCost;
      groupedProcess.isRequired =
        groupedProcess.isRequired ||
        Boolean(process.is_required || process.isRequired);

      if (Number.isFinite(layerFactor) && Math.abs(layerFactor - 1) > 0.001) {
        groupedProcess.layerNotes.add(
          `${(layerFactor * 100).toFixed(0)}% layer`,
        );
      }
    });

    return Array.from(groupedProcesses.values())
      .sort((left, right) => left.sequenceOrder - right.sequenceOrder)
      .map((process) => ({
        ...process,
        estimatedArea: Number(process.estimatedArea || 0),
        estimatedCost: Number(process.estimatedCost || 0),
        layerNote:
          process.layerNotes.size === 1
            ? Array.from(process.layerNotes)[0]
            : process.layerNotes.size > 1
              ? `${process.layerNotes.size} layer variants`
              : null,
      }));
  };

  const formatParentDisplay = (element) => {
    const parentName = element.parent_name || element.frame_name || "";
    const parentCode = element.parent_element_code || element.frame_code || "";

    if (parentName) {
      return parentName;
    }

    return parentCode || "Unassigned";
  };

  const getConditionSummary = (element) => {
    const corrosionCategory =
      element.resolved_corrosion_category ||
      element.inspection_corrosion_category ||
      element.project_corrosion_category ||
      element.corrosion_category ||
      null;
    const damageSeverity =
      element.resolved_damage_severity ||
      element.inspection_damage_severity ||
      element.project_damage_severity ||
      element.damage_level ||
      null;

    return (
      element.resolved_combination_code ||
      element.base_combination_code ||
      `${corrosionCategory || ""}${damageSeverity || ""}` ||
      "N/A"
    );
  };

  const getGroupCostSummary = (items = []) =>
    items.reduce(
      (totals, item) => ({
        processSubtotal:
          totals.processSubtotal + Number(item.repair_process_cost_total || 0),
        allocatedMobilization:
          totals.allocatedMobilization +
          Number(item.allocated_mobilization_cost || 0),
        totalEstimatedCost:
          totals.totalEstimatedCost +
          Number(item.estimated_total_cost || item.estimated_cost || 0),
      }),
      {
        processSubtotal: 0,
        allocatedMobilization: 0,
        totalEstimatedCost: 0,
      },
    );

  const filteredSpans = spans.filter(
    (span) => matchesSearch(span) && matchesFilter(span),
  );
  const canManagePanelBays =
    projectStatus === "pending" && typeof onManagePanelBays === "function";
  const canShowCompletionButtons =
    projectStatus === "pending" &&
    (typeof onMarkCompleted === "function" ||
      typeof onMarkUndamaged === "function");

  const shouldGroupByParent = filteredSpans.some(
    (span) =>
      span.parent_element_id ||
      span.parent_element_code ||
      span.parent_name ||
      span.frame_name,
  );

  const groupedSpans = shouldGroupByParent
    ? filteredSpans.reduce((groups, span) => {
        const groupKey =
          span.parent_element_id ||
          span.parent_element_code ||
          span.parent_name ||
          span.frame_name ||
          "__ungrouped__";

        if (!groups[groupKey]) {
          groups[groupKey] = {
            key: groupKey,
            label: formatParentDisplay(span),
            items: [],
          };
        }

        groups[groupKey].items.push(span);
        return groups;
      }, {})
    : {
        __all__: {
          key: "__all__",
          label: null,
          items: filteredSpans,
        },
      };

  const groupedSpanList = Object.values(groupedSpans);

  if (spans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No {elementLabelPlural.toLowerCase()} in this repair project
      </div>
    );
  }

  if (filteredSpans.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No {elementLabelPlural.toLowerCase()} match your search or filter
        criteria
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groupedSpanList.map((group) => {
        const hasGroupLabel = Boolean(group.label);
        const isGroupExpanded = !hasGroupLabel || expandedGroups.has(group.key);
        const groupCostSummary = getGroupCostSummary(group.items);

        return (
          <div
            key={group.key}
            className={
              hasGroupLabel
                ? "overflow-hidden rounded-xl border border-gray-200 bg-slate-50 shadow-sm"
                : "space-y-4"
            }
          >
            {hasGroupLabel && (
              <div className="w-full bg-slate-50 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={(event) => toggleGroupExpansion(group.key, event)}
                    className="min-w-0 flex flex-1 flex-wrap items-center gap-x-4 gap-y-2 text-left"
                  >
                    <FontAwesomeIcon
                      icon={isGroupExpanded ? faChevronDown : faChevronRight}
                      className="w-4 h-4 text-gray-500"
                    />
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {parentGroupLabelSingular}
                    </span>
                    <h3 className="text-base font-semibold text-gray-900">
                      {group.label}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                      <span className="text-gray-600">
                        Process Cost:{" "}
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(groupCostSummary.processSubtotal)}
                        </span>
                      </span>
                      <span className="text-gray-600">
                        Mobilization Cost:{" "}
                        <span className="font-semibold text-gray-900">
                          {formatCurrency(
                            groupCostSummary.allocatedMobilization,
                          )}
                        </span>
                      </span>
                      <span className="text-gray-600">
                        Total Cost:{" "}
                        <span className="font-bold text-emerald-600">
                          {formatCurrency(groupCostSummary.totalEstimatedCost)}
                        </span>
                      </span>
                    </div>
                  </button>
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-gray-600">
                      {group.items.length}{" "}
                      {group.items.length === 1
                        ? elementLabelSingular.toLowerCase()
                        : elementLabelPlural.toLowerCase()}
                    </div>
                    {canManagePanelBays && (
                      <button
                        type="button"
                        onClick={() =>
                          handleManagePanelBays({
                            key: group.key,
                            label: group.label,
                            items: group.items,
                            isGroupSelection: true,
                          })
                        }
                        className="flex items-center gap-1 rounded-md border border-blue-200 bg-blue-50 px-3 py-1.5 text-xs text-blue-700 transition-colors hover:bg-blue-100"
                        title="Record painted area for all grouped elements"
                      >
                        <FontAwesomeIcon icon={faFillDrip} />
                        Painted Area
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {isGroupExpanded && (
              <div
                className={
                  hasGroupLabel
                    ? "border-t border-gray-200 bg-white/70 px-4 py-4"
                    : ""
                }
              >
                <div
                  className={
                    hasGroupLabel
                      ? "space-y-3 border-l-2 border-slate-200 pl-4"
                      : "space-y-4"
                  }
                >
                  {group.items.map((span) => {
                    const isExpanded = expandedSpans.has(span.id);
                    const displayProcesses = getDisplayRepairProcesses(span);
                    const areaSummary = getElementAreaSummary(span);
                    const status =
                      span.element_status || span.span_status || "pending";
                    const spanElementId = span.id || span.element_id;
                    const isSavingCompletion =
                      completionActionElementId === spanElementId;

                    return (
                      <div
                        key={span.id}
                        className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
                      >
                        <div
                          className={`bg-gray-50 px-4 py-3 ${isExpanded ? "border-b border-gray-200" : ""}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <button
                                onClick={(event) =>
                                  toggleSpanExpansion(span.id, event)
                                }
                                className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                type="button"
                              >
                                <FontAwesomeIcon
                                  icon={
                                    isExpanded ? faChevronDown : faChevronRight
                                  }
                                  className="w-4 h-4"
                                />
                              </button>
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {span.element_name || span.span_name}
                                </h3>
                                <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                  <span>
                                    {formatNumber(areaSummary.totalArea)} m²
                                  </span>
                                  <span className="text-gray-400">/</span>
                                  <span
                                    className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                                      areaSummary.hasSavedActualPaintedArea
                                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                        : "border-orange-200 bg-orange-50 text-orange-700"
                                    }`}
                                    title={
                                      areaSummary.hasSavedActualPaintedArea
                                        ? "Saved actual painted area"
                                        : "Estimated painted area"
                                    }
                                  >
                                    {formatNumber(areaSummary.paintedArea)} m²
                                  </span>
                                </div>
                                <span className="text-sm font-medium text-gray-700">
                                  {getConditionSummary(span)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center space-x-3">
                              {getStatusBadge(status, span.completion_type)}
                              {canManagePanelBays && (
                                <button
                                  onClick={() => handleManagePanelBays(span)}
                                  className="px-3 py-1.5 bg-blue-50 text-blue-700 text-xs rounded-md border border-blue-200 hover:bg-blue-100 transition-colors flex items-center gap-1"
                                  type="button"
                                  title="Record actual painted area"
                                >
                                  <FontAwesomeIcon icon={faFillDrip} />
                                  Painted Area
                                </button>
                              )}
                              {canShowCompletionButtons && (
                                <button
                                  onClick={() => onMarkCompleted(span)}
                                  className="px-3 py-1.5 bg-emerald-50 text-emerald-700 text-xs rounded-md border border-emerald-200 hover:bg-emerald-100 transition-colors flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
                                  type="button"
                                  disabled={
                                    !projectStarted || isSavingCompletion
                                  }
                                  title={
                                    projectStarted
                                      ? `Mark ${elementLabelSingular.toLowerCase()} as completed`
                                      : `Start project to mark ${elementLabelSingular.toLowerCase()} as completed`
                                  }
                                >
                                  <FontAwesomeIcon icon={faCheckCircle} />
                                  Mark Completed
                                </button>
                              )}
                              {canShowCompletionButtons && (
                                <button
                                  onClick={() => onMarkUndamaged(span)}
                                  className="px-3 py-1.5 bg-white text-emerald-700 text-xs rounded-md border border-emerald-200 hover:bg-emerald-50 transition-colors flex items-center gap-1 disabled:cursor-not-allowed disabled:opacity-50"
                                  type="button"
                                  disabled={
                                    !projectStarted || isSavingCompletion
                                  }
                                  title={
                                    projectStarted
                                      ? `Mark ${elementLabelSingular.toLowerCase()} as undamaged`
                                      : `Start project to mark ${elementLabelSingular.toLowerCase()} as undamaged`
                                  }
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                  Mark Undamaged
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="border-t border-gray-200">
                            {span.inspection_id && (
                              <div className="px-4 py-3 bg-blue-50">
                                <h4 className="text-sm font-semibold text-blue-900 mb-2 flex items-center">
                                  <FontAwesomeIcon
                                    icon={faInfoCircle}
                                    className="mr-2"
                                  />
                                  Inspection Data
                                </h4>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  <div>
                                    <p className="text-xs text-blue-700">
                                      Inspection Date
                                    </p>
                                    <p className="text-sm font-medium text-blue-900">
                                      {formatDate(span.inspection_date)}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-blue-700">
                                      Inspector
                                    </p>
                                    <p className="text-sm font-medium text-blue-900">
                                      {span.inspector_name || "N/A"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-blue-700">
                                      Damage Area
                                    </p>
                                    <p className="text-sm font-medium text-blue-900">
                                      {span.inspection_damage_area
                                        ? `${formatNumber(span.inspection_damage_area)} m²`
                                        : "N/A"}
                                    </p>
                                  </div>
                                  <div>
                                    <p className="text-xs text-blue-700">
                                      Urgency
                                    </p>
                                    <div className="mt-1">
                                      {span.urgency_level
                                        ? getUrgencyBadge(span.urgency_level)
                                        : "N/A"}
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-3">
                                  <p className="text-xs text-blue-700 mb-2">
                                    Damage Split
                                  </p>
                                  <div className="flex space-x-4">
                                    <div className="flex items-center">
                                      <span className="text-xs text-blue-700 mr-2">
                                        A:
                                      </span>
                                      <span className="text-sm font-medium text-blue-900">
                                        {span.damage_percentage_a || 70}%
                                      </span>
                                    </div>
                                    <div className="flex items-center">
                                      <span className="text-xs text-blue-700 mr-2">
                                        B:
                                      </span>
                                      <span className="text-sm font-medium text-blue-900">
                                        {span.damage_percentage_b || 20}%
                                      </span>
                                    </div>
                                    <div className="flex items-center">
                                      <span className="text-xs text-blue-700 mr-2">
                                        C:
                                      </span>
                                      <span className="text-sm font-medium text-blue-900">
                                        {span.damage_percentage_c || 10}%
                                      </span>
                                    </div>
                                  </div>
                                </div>

                                {span.recommendations && (
                                  <div className="mt-3">
                                    <p className="text-xs text-blue-700">
                                      Recommendations
                                    </p>
                                    <p className="text-sm text-blue-900 mt-1">
                                      {span.recommendations}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}

                            <div className="px-4 py-3">
                              <h4 className="text-sm font-semibold text-gray-900 mb-3">
                                Repair Processes Required
                              </h4>
                              {span.estimate_error && (
                                <div className="mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                                  {span.estimate_error}
                                </div>
                              )}
                              {displayProcesses.length > 0 ? (
                                <div className="space-y-2">
                                  {displayProcesses.map((process, idx) => (
                                    <div
                                      key={process.processKey}
                                      className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded"
                                    >
                                      <div className="flex items-center space-x-3">
                                        <div>
                                          <span className="text-sm font-medium text-gray-700">
                                            {idx + 1}. {process.processName}
                                          </span>
                                          <div className="text-xs text-gray-500 mt-0.5">
                                            {formatNumber(
                                              process.estimatedArea,
                                            )}{" "}
                                            m²
                                            {process.layerNote && (
                                              <span>
                                                {" "}
                                                • {process.layerNote}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                        {process.isRequired && (
                                          <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded">
                                            Required
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-600">
                                        Est:{" "}
                                        {formatCurrency(process.estimatedCost)}
                                      </div>
                                    </div>
                                  ))}
                                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-semibold text-gray-900">
                                        Process Subtotal:
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        {formatCurrency(
                                          span.repair_process_cost_total || 0,
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-semibold text-gray-900">
                                        Allocated Mobilization:
                                      </span>
                                      <span className="text-sm font-semibold text-gray-900">
                                        {formatCurrency(
                                          span.allocated_mobilization_cost || 0,
                                        )}
                                      </span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <span className="text-sm font-semibold text-gray-900">
                                        Total Estimated Cost:
                                      </span>
                                      <span className="text-lg font-bold text-green-600">
                                        {formatCurrency(
                                          span.estimated_total_cost ||
                                            span.estimated_cost ||
                                            0,
                                        )}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500">
                                  No repair processes defined
                                </p>
                              )}
                            </div>

                            {span.span_notes && (
                              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                                <p className="text-xs text-gray-500">
                                  Completion Notes
                                </p>
                                <p className="text-sm text-gray-900 mt-1">
                                  {span.span_notes}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default RepairProcessTable;
